import WeighInForm from './WeighInForm';
import FormModal from '../../components/FormModal';
import ContestLabel from '../../components/ContestLabel';
import TeamLabel from '../../components/TeamLabel';
//import SessionLabel from '../../components/SessionLabel';
import {
  Button,
  Divider,
  Grid,
  Icon,
  Modal,
  Segment,
  Table,
} from '../../components';
import { formatName } from '../../common';

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { countBy, find, get, keyBy, map, mapValues, omit, pick } from 'lodash';

const ClearModal = ({ trigger, row, onClear }) => (
  <Modal trigger={trigger} basic>
    <Modal.Header>Clear weigh-in data?</Modal.Header>
    <Modal.Content>
      Do you really want to clear {row.description}?
    </Modal.Content>
    <Modal.Actions>
      <Button
        onClick={() => onClear(row.key)}
        /* TODO: Don't regen. */ negative
      >
        Clear
      </Button>
    </Modal.Actions>
  </Modal>
);

// TODO: Reject weigh-in dialog save, if any contest limits are violated?
const WeighInModal = ({ id, trigger, row, meta, onSave }) => {
  const Actions = ({ formProps, modal }) => (
    <Modal.Actions>
      <Button form={formProps.id} floated="right" positive>
        Save
      </Button>
      <Button onClick={modal.onCancel}>Cancel</Button>
      <Button onClick={modal.onReset} floated="left">
        Reset
      </Button>
    </Modal.Actions>
  );

  const defaultValues = {
    ...pick(row, 'isExcluded', 'bodyweight'),
    ...row.weighInDeclarations,
  };

  const onFormSubmit = values => {
    onSave({ ...defaultValues, ...values });
  };

  return (
    <FormModal
      id={id}
      trigger={trigger}
      header={`Weigh-in ${row.name}`}
      Form={WeighInForm}
      Actions={Actions}
      defaultValues={defaultValues}
      meta={meta}
      onSubmit={onFormSubmit}
    />
  );
};

const WeighInEditorHeader = ({ locked, meta }) => (
  <Table.Header>
    {/*
    <Table.Row>
      <Table.HeaderCell colSpan={3}>Athlete</Table.HeaderCell>
      <Table.HeaderCell colSpan={2}>Starting attempts</Table.HeaderCell>
      <Table.HeaderCell />
    </Table.Row>
    */}
    <Table.Row>
      <Table.HeaderCell width={1}>Nr</Table.HeaderCell>
      <Table.HeaderCell width={1}>Lot</Table.HeaderCell>
      <Table.HeaderCell>Name</Table.HeaderCell>
      <Table.HeaderCell width={2}>Team</Table.HeaderCell>
      <Table.HeaderCell width={3}>Contest</Table.HeaderCell>
      <Table.HeaderCell style={{ width: '5em' }}>
        <abbr title="Bodyweight">B.W.</abbr>
      </Table.HeaderCell>
      {meta.lifts.map(({ id, abbreviation, title }) => (
        <Table.HeaderCell key={id} width={1} style={{ width: '4em' }}>
          <abbr title={title}>{abbreviation}</abbr>1
        </Table.HeaderCell>
      ))}
      {!locked && <Table.HeaderCell width={2}>Actions</Table.HeaderCell>}
    </Table.Row>
  </Table.Header>
);

// TODO: Show age group only?

const WeighInEditorRow = ({ locked, id, row, meta, onSave, onClear }) => {
  const isExcluded = !!row.isExcluded;
  const bodyweightText = isExcluded
    ? '--'
    : row.bodyweight
      ? row.bodyweight.toFixed(2)
      : '';
  return (
    <Table.Row positive={row.isComplete} warning={isExcluded}>
      {/*
      <Table.Cell>
        {row.session && <SessionLabel session={row.session} />}
      </Table.Cell>
      */}
      <Table.Cell>
        {row.isComplete ? <strong>{row.startNumber}</strong> : '--'}
      </Table.Cell>
      <Table.Cell>{row.lotNumber}</Table.Cell>
      <Table.Cell>{row.name}</Table.Cell>
      <Table.Cell>
        {row.teams.map(team => (
          <TeamLabel key={team.id} team={team} />
        ))}
      </Table.Cell>
      <Table.Cell>
        {row.contests.map(contest => (
          <ContestLabel key={contest.id} contest={contest} />
        ))}
      </Table.Cell>
      <Table.Cell>{bodyweightText}</Table.Cell>
      {meta.lifts.map(lift => (
        <Table.Cell key={lift.id}>
          {isExcluded ? '--' : row.weighInDeclarations[lift.uuid]}
        </Table.Cell>
      ))}
      {!locked && (
        <Table.Cell>
          <WeighInModal
            id={`${id}--weigh-in-modal`}
            trigger={<Button icon="edit" size="tiny" circular basic />}
            row={row}
            meta={meta}
            onSave={onSave}
          />
          {
            /*!row.isClear &&*/ <ClearModal
              trigger={
                <Button icon="eraser" size="tiny" circular negative basic />
              }
              row={row}
              onClear={onClear}
            />
          }
        </Table.Cell>
      )}
    </Table.Row>
  );
};

const WeighInEditorDivider = ({ locked, content }) => (
  <Table.Row>
    <Table.Cell colSpan={9 - (locked ? 1 : 0)}>
      <Divider horizontal>{content}</Divider>
    </Table.Cell>
  </Table.Row>
);

const WeighInEditor = ({ id, locked, session, meta, actions }) => {
  let nextStartNumber = 1;
  let previousBodyweightCategory;
  const rows = session.entries.map(entry => {
    const weighInDeclarations = mapValues(keyBy(meta.lifts, 'uuid'), ({ id }) =>
      get(find(entry.weighInDeclarations, { lift: id }), 'weight'),
    );
    const name = formatName(entry);

    const { bodyweightCategory } = get(entry, 'group.contest');
    const divider =
      bodyweightCategory.id !== get(previousBodyweightCategory, 'id')
        ? bodyweightCategory.title
        : undefined;
    previousBodyweightCategory = bodyweightCategory;

    const { weighInDatum } = entry;
    const isExcluded = get(weighInDatum, 'isExcluded');
    const bodyweight = get(weighInDatum, 'bodyweight');
    const isClear =
      bodyweight === undefined &&
      meta.lifts.every(({ uuid }) => !weighInDeclarations[uuid]);
    const isComplete =
      isExcluded ||
      (bodyweight !== undefined &&
        meta.lifts.every(({ uuid }) => !!weighInDeclarations[uuid]));
    const startNumber = !isExcluded ? nextStartNumber++ : 0;
    return {
      key: entry.primaryKey,
      description: `weigh-in data for ${name}`,
      session: get(find(meta.groups, pick(entry.group, 'id')), 'session'),
      startNumber,
      lotNumber: entry.lotNumber,
      name,
      teams: entry.teams,
      contests: entry.contests,
      isExcluded,
      bodyweight,
      weighInDeclarations,
      isClear,
      isComplete,
      divider,
      meta: { entry },
    };
  });

  const { false: incompleteCount } = countBy(rows, 'isComplete');
  const isComplete = !incompleteCount;

  const onRowSave = (row, values) => {
    const weighInDeclarations = pick(values, map(meta.lifts, 'uuid'));
    values = omit(values, map(meta.lifts, 'uuid'));
    actions.save(row.key, values, weighInDeclarations);
  };

  const onRowClear = row => {
    actions.clear(row.key);
  };

  const onCompleteWeighIn = () => {
    actions.complete(session, rows);
  };

  return (
    <section id={id}>
      <Segment attached="top" style={{ fontSize: '1.25em' }}>
        <Grid columns="equal" verticalAlign="middle">
          <Grid.Column>
            {isComplete && <Icon name="checkmark" color="green" />}
            {isComplete
              ? 'All athletes have been weighed.'
              : [incompleteCount, ' athlete(s) to be weighed.']}
          </Grid.Column>
          <Grid.Column textAlign="right">
            {locked ? (
              <span>
                Weigh has been completed
                <Icon name="lock" color="red" />
              </span>
            ) : (
              <Button
                icon="save"
                content="Complete weigh-in"
                disabled={!isComplete}
                onClick={onCompleteWeighIn}
                primary
              />
            )}
          </Grid.Column>
        </Grid>
      </Segment>
      <Table fixed singleLine compact attached="bottom">
        <WeighInEditorHeader locked={locked} meta={meta} />
        <Table.Body>
          {rows.map((row, index) => (
            <Fragment key={row.key}>
              {row.divider && <WeighInEditorDivider content={row.divider} />}
              <WeighInEditorRow
                locked={locked}
                id={`${id}--row-${row.key}`}
                row={row}
                meta={{ ...meta, entry: row.meta.entry }}
                onSave={values => onRowSave(row, values)}
                onClear={() => onRowClear(row)}
              />
            </Fragment>
          ))}
        </Table.Body>
      </Table>
    </section>
  );
};

WeighInEditor.propTypes = {
  id: PropTypes.string.isRequired,
  session: PropTypes.object.isRequired,
  actions: PropTypes.shape({
    save: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    complete: PropTypes.func.isRequired,
  }).isRequired,
  meta: PropTypes.object.isRequired,
};

export default WeighInEditor;



// WEBPACK FOOTER //
// ./src/pages/WeighIn/WeighInEditor.js